import { ArchiveIcon, ChevronRightIcon } from "lucide-react"
import { Fragment, type DOMAttributes, type MouseEvent, type ReactNode } from "react"
import { Link } from "react-router"
import { Badge } from "@/primitives/badge.tsx"
import { Button } from "@/primitives/button.tsx"
import { cn } from "@/primitives/utils"
import { Key } from "./key.tsx"
import { useStickyTop } from "./page-chrome.tsx"
import { RowActions, type RowAction } from "./row-actions.tsx"
import type { TicketCounts } from "./stat.ts"
import { TicketProgress } from "./stat.tsx"
import { MoreChip } from "./ticket-glyphs.tsx"
import { opensPeek } from "./ticket-peek.ts"
import { useViewport } from "./viewport.tsx"

/*
 * The head of a group of rows in a list, such as one Project's tickets on My tickets: a sunken
 * band that sticks under the page's chrome while its rows scroll beneath, so the Member always
 * knows whose rows they are reading. It collapses its group, and the page keeps which groups are
 * collapsed (per viewer, in localStorage), from its chevron or a click on its empty part. It draws
 * only the hairline under it: the one above is the toolbar's, or the previous group's last row's,
 * and it would double if it drew its own.
 */

/** What a drag sensor listens for on the band: a press of the pointer, the mouse or a finger. */
export type DragListeners = Pick<
  DOMAttributes<HTMLElement>,
  "onPointerDown" | "onMouseDown" | "onTouchStart"
>

/**
 * `glyph` goes before the `label` (a Project's tile, an Epic's bolt), `labelKey` is a key shown
 * before the label as part of it (an Epic's), `href` makes the label a link, which with `onPeek`
 * opens the Ticket peek on a plain click (`opensPeek`) and leaves any other to the link, as a
 * ticket's row does, and `state` goes with the link, as a ticket row's does. `secondary` is a key
 * shown after it, then the `count` of rows. At the end, `chips` (an Epic's Labels and Area, each a
 * chip and its name), then `progress`: how its tickets stand, as `TicketProgress` shows them, in
 * one column down the list and shown even with none to count. `badges` say what the group's owner
 * is, as "Archived" does for an archived Project's tickets, in the same outline Badge the Projects
 * list gives its row. `rows` is the id of the element holding the group's rows, which the chevron
 * says it shows and hides.
 *
 * `drag` makes the group draggable, as an open Epic is in the Epic order: its sensor's `listeners`
 * (the pointer's, or the mouse's and a finger's long-press on a touch tablet) drag it from anywhere
 * on the band, and the keyboard from its `grip`, which stands in the band's left padding. On touch a
 * draggable band keeps the long-press to itself (`no-callout`). `more` is the band's ⋯ at its end,
 * revealed as a row's is, named by `label`: what the group's owner offers where it doesn't drag, as
 * an Epic's Move up and Move down on `compact`. `highlighted` flashes the band with a row's highlight
 * (`rowHighlight`'s fill and bar) on `compact`, where a move from its ⋯ has just put it, so the eye
 * finds it in the list it reordered.
 *
 * On `compact` the band is two lines, at least 44px and 16px in, as a ticket's row is: the label on
 * the first, the band's whole width, wrapping by word rather than being cut; under it, in a line that
 * never wraps, the key, the `secondary`, the count, the badges, the chips folded into one "+N"
 * (`MoreChip`) naming them, and the progress at its end, its bar half as wide. The chevron and the
 * glyph sit level with the label's first line, and the ⋯ at the band's end, centred.
 *
 * `nested` heads a group inside another's, as an Epic's inside an Area's on a phone's Board: set in
 * past the outer band's chevron, on the page's surface rather than sunken, and scrolling under the
 * outer band, which stays stuck, rather than sticking over it.
 */
export function GroupHeader({
  collapsed,
  onToggle,
  glyph,
  label,
  secondary,
  count,
  progress,
  badges = [],
  href,
  state,
  labelKey,
  onPeek,
  chips = [],
  drag,
  more,
  highlighted = false,
  rows,
  nested = false,
}: {
  collapsed: boolean
  onToggle: () => void
  glyph: ReactNode
  label: string
  secondary?: string
  count: number
  progress?: TicketCounts
  badges?: readonly "archived"[]
  href?: string
  state?: unknown
  labelKey?: string
  onPeek?: () => void
  chips?: readonly { name: string; chip: ReactNode }[]
  drag?: { grip: ReactNode; listeners: DragListeners }
  more?: { label: string; items: readonly RowAction[] }
  highlighted?: boolean
  rows?: string
  nested?: boolean
}) {
  const top = useStickyTop()
  const { compact } = useViewport()
  const named = (
    <>
      {labelKey && <Key value={labelKey} />}
      <span className="truncate text-foreground">{label}</span>
    </>
  )
  const linked = (children: ReactNode, className: string) =>
    href ? (
      <Link
        to={href}
        {...(state !== undefined && { state })}
        {...(onPeek && {
          onClick: (event: MouseEvent) => {
            if (!opensPeek(event, compact)) return
            event.preventDefault()
            onPeek()
          },
        })}
        className={cn(
          "rounded-sm outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring",
          className,
        )}
      >
        {children}
      </Link>
    ) : (
      <span className={className}>{children}</span>
    )
  const chevron = (
    <Button
      variant="ghost"
      size="icon-xs"
      aria-expanded={!collapsed}
      aria-controls={rows}
      aria-label={labelKey ? `${labelKey} ${label}` : label}
      onClick={onToggle}
      // Open is the resting state here, so it doesn't take the selected fill an open menu does.
      className="-ml-1 aria-expanded:bg-transparent aria-expanded:text-muted-foreground aria-expanded:hover:bg-accent aria-expanded:hover:text-foreground"
    >
      <ChevronRightIcon
        className={cn("transition-transform duration-100 ease-snappy", !collapsed && "rotate-90")}
      />
    </Button>
  )
  const archived = badges.map((badge) => (
    <Badge key={badge} variant="outline">
      <ArchiveIcon aria-hidden />
      Archived
    </Badge>
  ))
  const actions = more && <RowActions label={more.label} items={more.items} />
  return (
    <div
      role="heading"
      aria-level={nested ? 3 : 2}
      {...drag?.listeners}
      // The chevron is the keyboard's way to fold; a click anywhere else but a link is the pointer's,
      // and a Toggletip in it leaves it the click on the desk (`data-pressable`). A click in what the
      // band opened, its ⋯'s sheet, reaches it through React's tree alone and isn't the band's.
      data-pressable
      data-highlighted={highlighted || undefined}
      onClick={(event) => {
        const target = event.target as Element
        if (!event.currentTarget.contains(target) || target.closest("a, button")) return
        onToggle()
      }}
      className={cn(
        "group/row flex h-9 items-center gap-2 border-b border-border-subtle bg-surface-sunken px-6 text-ui font-medium compact:h-auto compact:min-h-11 compact:items-start compact:px-4 compact:py-1.5",
        drag ? "cursor-grab touch:no-callout" : "cursor-pointer",
        // A landing's flash, on `compact` alone: the band fills and its left edge takes the bar.
        "compact:transition-colors compact:duration-100 compact:ease-snappy compact:before:absolute compact:before:inset-y-0 compact:before:left-0 compact:before:w-0.5 compact:data-highlighted:bg-accent compact:data-highlighted:before:bg-ring",
        // A nested band isn't sticky, so it is the flash bar's containing block itself, and it
        // takes no layer, so it scrolls under the stuck band above it.
        nested ? "relative bg-background pl-12 compact:pl-10" : cn("sticky z-10", top),
      )}
    >
      {drag?.grip}
      {compact ? (
        <>
          {/* Each as tall as the label's first line, so they sit level with it however it wraps. */}
          <span className="flex h-lh items-center">{chevron}</span>
          <span className="flex h-lh items-center">{glyph}</span>
          <span className="min-w-0 flex-1">
            <span className="block wrap-break-word">{linked(label, "text-foreground")}</span>
            {/* One line under it, as a ticket row's meta line, so the label has the band's width. */}
            <span className="mt-0.5 flex min-w-0 items-center gap-2 font-normal">
              {labelKey && <Key value={labelKey} />}
              {secondary && <Key value={secondary} />}
              <span className="text-xs text-subtle-foreground tabular-nums">{count}</span>
              {archived}
              {chips.length > 0 && <MoreChip names={chips.map(({ name }) => name)} />}
              {progress && (
                <span className="ml-auto flex">
                  <TicketProgress counts={progress} width={10} />
                </span>
              )}
            </span>
          </span>
          {actions && <span className="flex self-center">{actions}</span>}
        </>
      ) : (
        <>
          {chevron}
          {glyph}
          {linked(named, "flex min-w-0 items-center gap-2")}
          {secondary && <Key value={secondary} />}
          <span className="text-xs font-normal text-subtle-foreground tabular-nums">{count}</span>
          {archived}
          <span className="ml-auto flex min-w-0 shrink-0 items-center gap-1.5">
            {chips.map(({ chip }, index) => (
              <Fragment key={index}>{chip}</Fragment>
            ))}
            {progress && (
              <span className="ml-2 flex">
                <TicketProgress counts={progress} width={20} />
              </span>
            )}
            {actions}
          </span>
        </>
      )}
    </div>
  )
}
